import { Cancel, CheckCircle, ExpandMore, Info } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import type { InvoiceInsight, InvoiceInsights } from '~/data/schemas/invoice';
import { CARD_SX } from '~/theme';
import { formatCurrency } from '~/utils/format';

export type ThemeColor = 'success' | 'error' | 'warning' | 'info' | 'default';

// Light background gradient + icon circle colors for each status
export const STATUS_BG: Record<ThemeColor, { bg: string; circle: string }> = {
  success: { bg: 'linear-gradient(to right, #E8F5E9, #FFFFFF)', circle: '#C8E6C9' },
  error: { bg: 'linear-gradient(to right, #FFEBEE, #FFFFFF)', circle: '#FFCDD2' },
  warning: { bg: 'linear-gradient(to right, #FFF3E0, #FFFFFF)', circle: '#FFE0B2' },
  info: { bg: 'linear-gradient(to right, #E3F2FD, #FFFFFF)', circle: '#BBDEFB' },
  default: { bg: 'linear-gradient(to right, #FAFAFA, #FFFFFF)', circle: '#EEEEEE' },
};

export const STATUS_CONFIG: Record<
  string,
  { label: string; verb: string; action: string; color: ThemeColor }
> = {
  // `label` is the outcome ("Approved"), `action` what the consultant would do
  // about it ("Approve") — the header card is a suggestion, so it uses `action`.
  approved: { label: 'Approved', verb: 'APPROVE', action: 'Approve', color: 'success' },
  rejected: { label: 'Rejected', verb: 'REJECT', action: 'Reject', color: 'error' },
  'partial-approval': {
    label: 'Partial Approval',
    verb: 'PARTIAL APPROVAL',
    action: 'Partial Approve',
    color: 'warning',
  },
  'pending-data': {
    label: 'Pending Data',
    verb: 'PENDING DATA',
    action: 'Pending Data',
    color: 'info',
  },
  reset: { label: 'Reset', verb: 'RESET', action: 'Reset', color: 'default' },
};

const SENTIMENT_CONFIG: Record<string, { label: string; color: 'success' | 'error' | 'info' }> = {
  'supports-approval': { label: 'Supports Approval', color: 'success' },
  'supports-rejection': { label: 'Supports Rejection', color: 'error' },
  informational: { label: 'Informational', color: 'info' },
};

function ContractReference({ section, quote }: { section?: string | null; quote?: string | null }) {
  if (!section && !quote) return null;

  return (
    <Box sx={{ mt: 1.5, pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
      {quote ? (
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          &ldquo;{quote}&rdquo;
        </Typography>
      ) : null}
      {section ? (
        <Typography variant="caption" color="text.secondary" display="block" mt={quote ? 0.5 : 0}>
          Contract {section}
        </Typography>
      ) : null}
    </Box>
  );
}

const SENTIMENT_ICON: Record<string, React.ReactNode> = {
  'supports-approval': <CheckCircle fontSize="small" color="success" />,
  'supports-rejection': <Cancel fontSize="small" color="error" />,
  informational: <Info fontSize="small" color="info" />,
};

function FindingAccordion({
  finding,
  expanded,
  onChange,
}: {
  finding: InvoiceInsight;
  expanded: boolean;
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
}) {
  const sentimentConfig = finding.sentiment ? SENTIMENT_CONFIG[finding.sentiment] : null;
  const sentimentIcon = finding.sentiment ? SENTIMENT_ICON[finding.sentiment] : null;

  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      disableGutters
      sx={{
        boxShadow: 'none',
        '&.MuiAccordion-root': {
          ':before': { content: 'none' },
        },
        border: 'none',
        borderRadius: '0 !important',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore color="primary" />}
        sx={{ px: 2, '& .MuiAccordionSummary-content': { my: 1.25 } }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25} flexGrow={1} mr={1} minWidth={0}>
          {sentimentIcon ? (
            <Box display="flex" flexShrink={0}>
              {sentimentIcon}
            </Box>
          ) : null}
          <Typography variant="subtitle2" flexGrow={1}>
            {finding.insight}
          </Typography>
          {sentimentConfig ? (
            <Chip
              label={sentimentConfig.label}
              color={sentimentConfig.color}
              size="small"
              variant="outlined"
              sx={{ flexShrink: 0 }}
            />
          ) : null}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2, pl: 5.25 }}>
        {finding.reasoning ? (
          <Typography variant="body2" color="text.secondary">
            {finding.reasoning}
          </Typography>
        ) : null}
        <ContractReference section={finding.contractSection} quote={finding.contractQuote} />
      </AccordionDetails>
    </Accordion>
  );
}

/** One figure and what it is. The amounts used to be a tinted hero card and a loose sentence underneath; side by side they read as the pair of numbers they are. */
function Figure({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Box minWidth={0}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} sx={{ color: color ?? 'text.primary' }}>
        {value}
      </Typography>
    </Box>
  );
}

/** What to request on a proposal. The model fills this in on settled verdicts too, so the heading only promises a pending decision when there is one. */
function RequestList({ items, unsettled }: { items: string[]; unsettled: boolean }) {
  return (
    <Box sx={{ ...CARD_SX, p: 1.5 }}>
      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
        {unsettled ? 'Before deciding, request' : 'Worth requesting'}
      </Typography>
      <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
        {items.map((item, index) => (
          <Typography
            key={index}
            component="li"
            variant="body2"
            sx={{ '&:not(:last-of-type)': { mb: 0.5 } }}
          >
            {item}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

/** Filters out the confidence insight (added by mapping layer) — the header shows it. */
export const selectFindings = (insights: InvoiceInsights): InvoiceInsight[] =>
  insights.insights.filter((i) => i.sentiment && !i.insight.startsWith('AI Confidence:'));

/**
 * What the review reckons the invoice shouldn't have been billed: the whole
 * amount on a rejection, the disallowed part on a partial approval. Null when the
 * verdict doesn't imply any, or the amounts needed to work it out are missing.
 */
export function getIdentifiedSavings(
  insights: InvoiceInsights,
  invoiceAmount: number | null,
): number | null {
  if (invoiceAmount == null) return null;
  if (insights.status === 'rejected') return invoiceAmount;
  if (insights.status === 'partial-approval' && insights.reviewedAmount != null) {
    return invoiceAmount - insights.reviewedAmount;
  }
  return null;
}

export function ReviewContent({
  insights,
  invoiceAmount,
  currencyCode,
}: {
  insights: InvoiceInsights;
  invoiceAmount: number | null;
  currencyCode: string;
}) {
  const status = insights.status;
  const findings = selectFindings(insights);
  const missingEvidence = insights.missingEvidence ?? [];

  // Every finding starts open — the reasoning is the point of opening the dialog.
  // Tracking what's *collapsed* is what makes "all open" the initial state without
  // seeding the set from `findings`.
  const [collapsedIndexes, setCollapsedIndexes] = useState<ReadonlySet<number>>(
    () => new Set<number>(),
  );

  const handleAccordionChange =
    (index: number) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setCollapsedIndexes((prev) => {
        const next = new Set(prev);
        if (isExpanded) next.delete(index);
        else next.add(index);
        return next;
      });
    };

  const savings = getIdentifiedSavings(insights, invoiceAmount);
  const suggested =
    status === 'partial-approval' && insights.reviewedAmount != null
      ? insights.reviewedAmount
      : null;
  const hasFigures = (savings != null && savings > 0) || suggested != null;

  return (
    <Stack spacing={2.5}>
      {hasFigures ? (
        <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap sx={{ ...CARD_SX, p: 1.5 }}>
          {savings != null && savings > 0 ? (
            <Figure
              label="Potential reduction"
              value={formatCurrency({ value: savings, currencyCode })}
              color="success.dark"
            />
          ) : null}
          {suggested != null ? (
            <Figure
              label="Suggested amount"
              value={formatCurrency({ value: suggested, currencyCode })}
            />
          ) : null}
        </Stack>
      ) : null}

      {missingEvidence.length > 0 ? (
        <RequestList items={missingEvidence} unsettled={status === 'pending-data'} />
      ) : null}

      {findings.length > 0 ? (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" mb={1}>
            Findings ({findings.length})
          </Typography>
          <Box sx={{ ...CARD_SX, overflow: 'hidden' }}>
            {findings.map((finding, index) => (
              <Box key={index}>
                {index > 0 && <Divider />}
                <FindingAccordion
                  finding={finding}
                  expanded={!collapsedIndexes.has(index)}
                  onChange={handleAccordionChange(index)}
                />
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}
    </Stack>
  );
}
